import {
  Activity,
  Bookmark,
  BookOpen,
  Box,
  Calendar,
  DollarSign,
  Edit3,
  Hash,
  Layers,
  Package,
  Plus,
  QrCode
} from 'lucide-react';
import React, { useEffect, useState, useRef } from 'react';
import { DataTable } from '../../../../components/shared/DataTable/DataTable';
import { FilterSelect } from '../../../../components/shared/FilterSelect/FilterSelect';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import Modal from '../../../../components/shared/Modal/Modal';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { TableSearch } from '../../../../components/shared/TableSearch/TableSearch';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction, deleteAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../components/shared/DetailPanel/DetailPanel';
import { DetailAlert, DetailField, DetailFieldList, DetailHeaderButton, DetailIdentity } from '../../../../components/shared/DetailPanel/DetailBlocks';
import { useClickOutside, isDetailKeepZone } from '../../../../hooks/useClickOutside';
import { CATEGORY_OPTIONS, STATUS_OPTIONS } from '../../../../constants/inventory';
import { MOCK_BOOKS } from '../../../../mocks/Books.mocks';
import { Book } from '../../../../types/Book.types';
import { useToast } from '../../../../hooks/useToast';
import { listBooks, createBook, updateBook } from '../../../../services/BooksService';
import './BooksView.css';

const STATUS_LABELS: Record<Book['status'], string> = {
  editorial: 'Editorial',
  printing: 'Impresión',
  warehouse: 'Almacén',
  distributed: 'Distribuido',
  'out-of-stock': 'Sin Stock'
};

const STATUS_COLORS: Record<Book['status'], { border: string; text: string; bg: string }> = {
  editorial: { border: 'rgba(59, 130, 246, 0.4)', text: '#60a5fa', bg: 'rgba(59, 130, 246, 0.05)' },
  printing: { border: 'rgba(234, 179, 8, 0.4)', text: '#facc15', bg: 'rgba(234, 179, 8, 0.05)' },
  warehouse: { border: 'rgba(168, 85, 247, 0.4)', text: '#c084fc', bg: 'rgba(168, 85, 247, 0.05)' },
  distributed: { border: 'rgba(100, 255, 218, 0.4)', text: 'var(--color-brand-green)', bg: 'rgba(100, 255, 218, 0.05)' },
  'out-of-stock': { border: 'rgba(239, 68, 68, 0.4)', text: '#f87171', bg: 'rgba(239, 68, 68, 0.05)' }
};

interface BooksViewProps {
  onMenuOpen?: () => void;
  menuCloseSignal?: number;
}

export const BooksView = ({
  onMenuOpen = () => { },
  menuCloseSignal = 0
}: BooksViewProps) => {
  const { notify } = useToast();
  const [books, setBooks] = useState<Book[]>([]);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [deleteModalBook, setDeleteModalBook] = useState<Book | null>(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  // Sustituye el temporizador simulado que había antes: ahora el spinner dura
  // lo que tarde el catálogo real (listBooks cae a MOCK_BOOKS si falla).
  useEffect(() => {
    let cancelled = false;
    listBooks()
      .then(list => { if (!cancelled) setBooks(list); })
      .catch(err => {
        console.error('Error fetching books:', err);
        if (!cancelled) setBooks(MOCK_BOOKS);
      })
      .then(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const closeLocalMenus = () => {
    setIsCategoryFilterOpen(false);
    setIsStatusFilterOpen(false);
  };

  const initialFormStateRef = useRef<Omit<Book, 'id' | 'lastUpdated'>>({
    title: '',
    author: '',
    isbn: '',
    sku: '',
    publisher: '',
    status: 'warehouse',
    stock: 0,
    price: 15.00,
    category: ''
  });

  const [formData, setFormData] = useState<Omit<Book, 'id' | 'lastUpdated'>>({
    title: '',
    author: '',
    isbn: '',
    sku: '',
    publisher: '',
    status: 'warehouse',
    stock: 0,
    price: 15.00,
    category: ''
  });

  const isFormDirty = () => {
    const init = initialFormStateRef.current;
    return (
      formData.title !== init.title ||
      formData.author !== init.author ||
      formData.isbn !== init.isbn ||
      formData.sku !== init.sku ||
      formData.publisher !== init.publisher ||
      formData.status !== init.status ||
      formData.stock !== init.stock ||
      formData.price !== init.price ||
      formData.category !== init.category
    );
  };

  useEffect(() => {
    if (selectedBook) {
      const current = books.find(b => b.id === selectedBook.id);
      if (current) {
        setSelectedBook(current);
      } else {
        setSelectedBook(null);
      }
    }
  }, [books]);

  useEffect(() => {
    if (selectedBook && !isFormOpen) {
      const initialData = {
        title: selectedBook.title,
        author: selectedBook.author,
        isbn: selectedBook.isbn,
        sku: selectedBook.sku,
        publisher: selectedBook.publisher,
        status: selectedBook.status,
        stock: selectedBook.stock,
        price: selectedBook.price,
        category: selectedBook.category
      };
      initialFormStateRef.current = initialData;
      setFormData(initialData);
    }
  }, [selectedBook, isFormOpen]);

  useEffect(() => {
    if (menuCloseSignal > 0) {
      closeLocalMenus();
    }
  }, [menuCloseSignal]);

  // Clic fuera: cierra los desplegables y el panel (con el aviso de cambios sin
  // guardar de siempre). Las zonas con data-keep-detail y los portales no cierran.
  useClickOutside(isDetailKeepZone, () => {
    closeLocalMenus();

    if (isFormOpen) {
      if (isFormDirty()) {
        setShowDiscardModal(true);
      } else {
        setSelectedBook(null);
        setIsFormOpen(false);
      }
    } else if (selectedBook) {
      setSelectedBook(null);
    }
  }, true);

  const handleCreateNewClick = () => {
    setSelectedBook(null);
    const initialData = {
      title: '',
      author: '',
      isbn: '',
      sku: '',
      publisher: '',
      status: 'warehouse' as const,
      stock: 0,
      price: 19.99,
      category: 'Ficción'
    };
    initialFormStateRef.current = initialData;
    setFormData(initialData);
    setIsFormOpen(true);
  };

  const handleEditClick = (book: Book) => {
    setSelectedBook(book);
    const initialData = {
      title: book.title,
      author: book.author,
      isbn: book.isbn,
      sku: book.sku,
      publisher: book.publisher,
      status: book.status,
      stock: book.stock,
      price: book.price,
      category: book.category
    };
    initialFormStateRef.current = initialData;
    setFormData(initialData);
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.author.trim() || !formData.isbn.trim()) {
      notify('Aviso', 'El título, el autor y el ISBN son obligatorios.', 'warning');
      return;
    }
    // El duplicado de SKU lo rechaza el backend (books_create/update → 409).
    const payload = { ...formData, sku: formData.sku.replace(/\s/g, '').toUpperCase() };

    try {
      if (selectedBook) {
        const updated = await updateBook(selectedBook.id, payload);
        // El backend no guarda stock: lo real se deriva de stock_movements.
        const merged: Book = { ...selectedBook, ...payload, ...updated };
        setBooks(prev => prev.map(b => b.id === selectedBook.id ? merged : b));
        setSelectedBook(merged);
        setIsFormOpen(false);
        notify('Éxito', 'Obra actualizada exitosamente', 'info');
      } else {
        const created = await createBook(payload);
        const merged: Book = { ...payload, ...created, lastUpdated: created.lastUpdated || new Date().toISOString() };
        setBooks(prev => [...prev, merged]);
        setSelectedBook(merged);
        setIsFormOpen(false);
        notify('Éxito', 'Obra registrada exitosamente', 'info');
      }
    } catch (err) {
      console.error('Error saving book:', err);
      notify('Error', err instanceof Error ? err.message : 'Error al guardar la obra.', 'urgent');
    }
  };

  const handleDeleteBook = (bookId: string) => {
    const book = books.find(b => b.id === bookId);
    if (!book) return;
    setDeleteModalBook(book);
  };

  const confirmDeleteBook = () => {
    if (!deleteModalBook) return;
    setBooks(prev => prev.filter(b => b.id !== deleteModalBook.id));
    setSelectedBook(null);
    setIsFormOpen(false);
    setDeleteModalBook(null);
    notify('Éxito', 'Obra eliminada exitosamente', 'info');
  };

  const filteredBooks = books.filter(book => {
    const matchesSearch =
      book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.isbn.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.id.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || book.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || book.category === categoryFilter;

    return matchesSearch && matchesStatus && matchesCategory;
  });

  const columns = [
    { id: 'id', header: 'ID_REF', className: 'books-view-table-td-id' },
    { id: 'title', header: 'Título', className: 'books-view-table-td-bright' },
    { id: 'author', header: 'Autor' },
    { id: 'isbn', header: 'ISBN' },
    { id: 'sku', header: 'SKU', className: 'books-view-table-td-id' },
    {
      id: 'status',
      header: 'Estado',
      render: (book: Book) => {
        const colors = STATUS_COLORS[book.status] || { border: 'var(--color-brand-blue-lighter)', text: 'inherit', bg: 'transparent' };
        return (
          <span
            className="books-details-status"
            style={{ borderColor: colors.border, color: colors.text, backgroundColor: colors.bg }}
          >
            {STATUS_LABELS[book.status]}
          </span>
        );
      }
    },
    { id: 'stock', header: 'Stock', className: 'books-view-table-td-bright' },
    {
      id: 'price',
      header: 'Precio',
      render: (book: Book) => <span className="books-details-price">€{book.price.toFixed(2)}</span>
    }
  ];

  return (
    <div onClick={closeLocalMenus}>
      <MasterDetailLayout isOpen={Boolean(selectedBook || isFormOpen)} drawer>
        <div className="books-view-container">
          <ModuleHeader
            title="Maestro_Libros"
            subtitle="Mantenimiento del catálogo base de obras: producción, adquisición, stock y comercialización."
          />

          <div className="books-view-filters-container">
            <div className="books-view-filters-row">
              <TableSearch
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="BUSCAR LIBRO POR TÍTULO, AUTOR, ISBN, SKU, ID..."
              />

              <div className="books-view-filter-group">
                <FilterSelect
                  isOpen={isStatusFilterOpen}
                  onToggle={(next) => {
                    closeLocalMenus();
                    onMenuOpen();
                    setIsStatusFilterOpen(next);
                  }}
                  onSelect={setStatusFilter}
                  value={statusFilter}
                  options={STATUS_OPTIONS}
                  icon={Activity}
                  allLabel="TODOS_LOS_ESTADOS"
                />

                <FilterSelect
                  isOpen={isCategoryFilterOpen}
                  onToggle={(next) => {
                    closeLocalMenus();
                    onMenuOpen();
                    setIsCategoryFilterOpen(next);
                  }}
                  onSelect={setCategoryFilter}
                  value={categoryFilter}
                  options={CATEGORY_OPTIONS}
                  icon={Box}
                  allLabel="TODAS_LAS_CATEGORIAS"
                />
              </div>

              <button
                onClick={(e) => { e.stopPropagation(); closeLocalMenus(); onMenuOpen(); handleCreateNewClick(); }}
                className="books-view-new-btn"
              >
                <Plus size={14} />
                Añadir_Libro
              </button>
            </div>
          </div>

          <DataTable
            data={filteredBooks}
            columns={columns}
            getRowId={(book) => book.id}
            onRowClick={(book) => { setSelectedBook(book); setIsFormOpen(false); }}
            selectedRowId={selectedBook?.id}
            showPagination={true}
            totalRecords={books.length}
            isLoading={isLoading}
            actionsColumn={(book) => (
              <RowActionsMenu
                onOpenChange={(open) => {
                  if (open) {
                    closeLocalMenus();
                    onMenuOpen();
                  }
                }}
                actions={[
                  viewAction(() => {
                    setSelectedBook(book);
                    setIsFormOpen(false);
                  }),
                  editAction(() => handleEditClick(book)),
                  deleteAction(() => handleDeleteBook(book.id))
                ]}
              />
            )}
          />
        </div>

        {isFormOpen ? (
          <SidebarForm
            drawer
            title={selectedBook ? 'Editar_Obra' : 'Nueva_Obra'}
            onClose={() => {
              if (isFormDirty()) {
                setShowDiscardModal(true);
              } else {
                setSelectedBook(null);
                setIsFormOpen(false);
              }
            }}
            submitLabel={selectedBook ? 'ACTUALIZAR' : 'GUARDAR'}
            onSubmit={handleSave}
            showDelete={Boolean(selectedBook)}
            onDelete={() => selectedBook && handleDeleteBook(selectedBook.id)}
          >
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Título_Del_Libro</label>
              <div className="sidebar-form-input-wrapper">
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, title: value }));
                  }}
                  className="sidebar-form-input"
                  placeholder="EJ. CIEN AÑOS DE SOLEDAD"
                />
              </div>
            </div>

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Autor_De_La_Obra</label>
              <div className="sidebar-form-input-wrapper">
                <input
                  type="text"
                  required
                  value={formData.author}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, author: value }));
                  }}
                  className="sidebar-form-input"
                  placeholder="EJ. GABRIEL GARCÍA MÁRQUEZ"
                />
              </div>
            </div>

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Código_ISBN</label>
              <div className="sidebar-form-input-wrapper">
                <input
                  type="text"
                  required
                  value={formData.isbn}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, isbn: value }));
                  }}
                  className="sidebar-form-input"
                  placeholder="EJ. 978-0307474728"
                />
              </div>
            </div>

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Código_Interno_SKU</label>
              <div className="sidebar-form-input-wrapper">
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, sku: value }));
                  }}
                  className="sidebar-form-input"
                  placeholder="VACÍO = SE GENERA (LIB000123)"
                />
              </div>
            </div>

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Editorial_Publicadora</label>
              <div className="sidebar-form-input-wrapper">
                <input
                  type="text"
                  required
                  value={formData.publisher}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, publisher: value }));
                  }}
                  className="sidebar-form-input"
                  placeholder="EJ. SUDAMERICANA / MARCIAL PONS"
                />
              </div>
            </div>

            <div className="sidebar-form-grid-2">
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Categoría</label>
                <div className="sidebar-form-input-wrapper">
                  <input
                    type="text"
                    required
                    value={formData.category}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData(prev => ({ ...prev, category: value }));
                    }}
                    className="sidebar-form-input"
                    placeholder="EJ. FICCIÓN"
                  />
                </div>
              </div>

              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Estado_Operativo</label>
                <div className="sidebar-form-input-wrapper">
                  <select
                    value={formData.status}
                    onChange={(e) => {
                      const value = e.target.value as Book['status'];
                      setFormData(prev => ({ ...prev, status: value }));
                    }}
                    className="sidebar-form-input"
                    style={{ textTransform: 'uppercase' }}
                  >
                    {Object.entries(STATUS_LABELS).map(([val, label]) => (
                      <option key={val} value={val}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="sidebar-form-grid-2">
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Stock_Existencias</label>
                <div className="sidebar-form-input-wrapper">
                  <input
                    type="number"
                    min={0}
                    required
                    value={formData.stock}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData(prev => ({ ...prev, stock: parseInt(value) || 0 }));
                    }}
                    className="sidebar-form-input"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Precio_De_Venta_PVP</label>
                <div className="sidebar-form-input-wrapper">
                  <input
                    type="number"
                    step="0.01"
                    min={0.01}
                    required
                    value={formData.price}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData(prev => ({ ...prev, price: parseFloat(value) || 0.0 }));
                    }}
                    className="sidebar-form-input"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </SidebarForm>
        ) : (
          selectedBook && (
            <DetailPanel
              drawer
              title="Ficha_De_Obra"
              onClose={() => { setSelectedBook(null); setIsFormOpen(false); }}
              headerActions={
                <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedBook)} />
              }
            >
              <DetailIdentity
                icon={BookOpen}
                reference="// REF_CATÁLOGO"
                name={selectedBook.title}
                subtitle={<>Por: <span className="detail-identity-highlight">{selectedBook.author}</span></>}
              />

              <DetailFieldList>
                <DetailField icon={QrCode} label="ISBN_Código:">{selectedBook.isbn}</DetailField>
                <DetailField icon={Hash} label="SKU_Interno:">{selectedBook.sku || 'Sin asignar'}</DetailField>
                <DetailField icon={Bookmark} label="Editorial:">{selectedBook.publisher}</DetailField>
                <DetailField icon={Layers} label="Categoría:">{selectedBook.category}</DetailField>
                <DetailField icon={Layers} label="Estado Operativo:">
                  <span
                    className="books-details-status"
                    style={{
                      borderColor: STATUS_COLORS[selectedBook.status]?.border || 'var(--color-brand-blue-lighter)',
                      color: STATUS_COLORS[selectedBook.status]?.text || 'inherit',
                      backgroundColor: STATUS_COLORS[selectedBook.status]?.bg || 'transparent'
                    }}
                  >
                    {STATUS_LABELS[selectedBook.status]}
                  </span>
                </DetailField>
                <DetailField icon={Package} label="Stock Físico:">
                  <span className={selectedBook.stock === 0 ? "books-details-stock-critical" : "books-details-stock-normal"}>
                    {selectedBook.stock} u.
                  </span>
                </DetailField>
                <DetailField icon={DollarSign} label="PVP Unitario:">
                  <span className="books-details-price">
                    €{selectedBook.price.toFixed(2)}
                  </span>
                </DetailField>
                <DetailField icon={Calendar} label="Último Registro:">
                  <span className="books-details-date">
                    {new Date(selectedBook.lastUpdated).toLocaleString()}
                  </span>
                </DetailField>
              </DetailFieldList>

              {selectedBook.stock < 50 && (
                <DetailAlert title="ALERTA: STOCK CRÍTICO">
                  El volumen actual de inventario está por debajo de los márgenes mínimos de operación comercial. Se requiere iniciar producción / orden de reposición de inmediato.
                </DetailAlert>
              )}
            </DetailPanel>
          )
        )}
      </MasterDetailLayout>

      <Modal
        isOpen={!!deleteModalBook}
        type="urgent"
        title="Eliminar libro"
        message={`¿Está seguro de que desea eliminar el libro "${deleteModalBook?.title}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDeleteBook}
        onCancel={() => setDeleteModalBook(null)}
      />

      <Modal
        isOpen={showDiscardModal}
        type="warning"
        title="Descartar cambios"
        message="¿Desea descartar los cambios no guardados y salir del formulario, o prefiere continuar editando?"
        confirmText="Descartar y salir"
        cancelText="Continuar editando"
        onConfirm={() => {
          setShowDiscardModal(false);
          setSelectedBook(null);
          setIsFormOpen(false);
        }}
        onCancel={() => setShowDiscardModal(false)}
      />
    </div>
  );
};
